import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Textfield from 'syf-component-library/ui/atoms/Textfield';
import { DATA_ANALYTICS, DATA_OBJECT, DATA_REASON, DATA_TYPE } from '../../../../const/analytics';
import { FAR_ANGLE_DOWN, FAR_ANGLE_UP, FAR_DOLLAR_SIGN, FAR_EXCLAMATION_CIRCLE } from '../../../../const/iconProps';
import { paymentOptions } from '../../../../const/paymentOptions';
import { getLoanProductPaymentAmount, isAutopayPaymentAmountOptionMutable, isInstallmentLoan, isIntroRateLoanPromoAvailableForAutopay, isMonthly, isPN4, isSecuredInstallmentLoanProduct, isSetpayLegacyProduct, isWaivedInterestAvailableForAutopay } from '../../../../helpers';
import getPaymentAmountFromOption from '../../../../helpers/getPaymentAmountFromOption';
import useApiMachine from '../../../../hooks/machines/useApiMachine';
import { t } from '../../../../strings/i18n';
import { useField } from '../../../../xform';
import SelectWithFocus from '../../../atoms/SelectWithFocus';
import { OtherOption, SelectWrapper } from '../subcomponents';
import { createPaymentAmountOption, getMonthlyOtherAmountHelpText } from './helpers';
const getAmountOptions = (apiContext) => {
    const accountSummary = apiContext.accountSummaryQueryResult.data;
    const enterpriseEligibility = apiContext.paymentEnterpriseEligibilityQueryResult.data;
    const productType = accountSummary.product.type;
    const otherAmountOption = {
        element: (_jsx(OtherOption, Object.assign({ "data-analytics": DATA_ANALYTICS.CLICK_TRACKING_EVENT, "data-cy": "other-amount-option", "data-object": DATA_OBJECT.BUTTON, "data-reason": DATA_REASON.PAYMENT_AMOUNT.OTHER_AMOUNT, "data-type": DATA_TYPE.MAKE_A_PAYMENT }, { children: t(`makePayment.${paymentOptions.OTHER_PAYMENT}`) }))),
        value: paymentOptions.OTHER_PAYMENT,
        label: t(`makePayment.${paymentOptions.OTHER_PAYMENT}`)
    };
    if (isMonthly(productType)) {
        const monthlyFixedPaymentAmountOption = createPaymentAmountOption(paymentOptions.MONTHLY_FIXED_PAYMENT_AMOUNT, apiContext);
        const waivedInterestAutopayAmountOption = createPaymentAmountOption(paymentOptions.WAIVED_INTEREST_AUTOPAY_AMOUNT, apiContext);
        if (isWaivedInterestAvailableForAutopay(enterpriseEligibility)) {
            return [
                monthlyFixedPaymentAmountOption,
                waivedInterestAutopayAmountOption
            ];
        }
        return [monthlyFixedPaymentAmountOption];
    }
    if (isPN4(productType)) {
        const pn4FixedPaymentAmountOption = createPaymentAmountOption(paymentOptions.PN4_FIXED_PAYMENT_AMOUNT, apiContext);
        return [pn4FixedPaymentAmountOption];
    }
    if (isSetpayLegacyProduct(productType)) {
        const monthlyPaymentOption = createPaymentAmountOption(paymentOptions.MONTHLY_PAYMENT, apiContext);
        return [monthlyPaymentOption, otherAmountOption];
    }
    if (isSecuredInstallmentLoanProduct(productType)) {
        const securedInstallmentFixedPaymentAmountOption = createPaymentAmountOption(paymentOptions.SECURED_INSTALLMENT_FIXED_PAYMENT_AMOUNT, apiContext);
        return [securedInstallmentFixedPaymentAmountOption];
    }
    const statementBalanceOption = createPaymentAmountOption(paymentOptions.STATEMENT_BALANCE, apiContext);
    const totalMinimumPaymentOption = createPaymentAmountOption(paymentOptions.MINIMUM_PAYMENT_DUE, apiContext);
    return [statementBalanceOption, totalMinimumPaymentOption, otherAmountOption];
};
const getDefaultOptions = (amountOptions, paymentOption) => {
    return amountOptions.find(({ value }) => value === paymentOption);
};
const AutopayAmountSelect = ({ id }) => {
    const [apiMachine] = useApiMachine();
    const { data: accountSummary } = apiMachine.context.accountSummaryQueryResult;
    const { data: paymentEnterpriseEligibility } = apiMachine.context.paymentEnterpriseEligibilityQueryResult;
    const { billing } = accountSummary;
    const productType = accountSummary.product.type;
    const amount = isInstallmentLoan(productType)
        ? getLoanProductPaymentAmount(accountSummary)
        : billing.totalMinimumPaymentDue;
    const paymentOptionField = useField('paymentOption');
    const paymentAmountField = useField('paymentAmount');
    const otherAmountKey = `${id}Other`;
    const isOtherAmount = paymentOptionField.value === paymentOptions.OTHER_PAYMENT;
    const amountOptions = getAmountOptions(apiMachine.context);
    const rightIconClosed = isAutopayPaymentAmountOptionMutable(paymentEnterpriseEligibility, productType) ? (_jsx(FontAwesomeIcon, { icon: FAR_ANGLE_DOWN, size: "lg" })) : null;
    const paymentAmount = isOtherAmount
        ? paymentAmountField.value
        : getPaymentAmountFromOption(paymentOptionField.value, apiMachine.context);
    const [otherAmountHelpText, setOtherAmountHelpText] = useState(getMonthlyOtherAmountHelpText(paymentAmountField.value, amount));
    const paymentOptionChangeHandler = ({ value }) => {
        paymentOptionField.setValue(value);
        paymentOptionField.blur();
    };
    const paymentAmountChangeHandler = ({ target }) => {
        paymentAmountField.setValue(target.value);
        setOtherAmountHelpText('');
    };
    const paymentAmountBlurHandler = () => {
        paymentAmountField.blur();
        setOtherAmountHelpText(getMonthlyOtherAmountHelpText(paymentAmountField.value, amount));
    };
    const shouldDisplayHelpTextForPaymentAmount = [
        paymentOptions.STATEMENT_BALANCE,
        paymentOptions.MINIMUM_PAYMENT_DUE,
        paymentOptions.SECURED_INSTALLMENT_FIXED_PAYMENT_AMOUNT
    ].includes(paymentOptionField.value);
    const autopayPaymentAmountHelpText = () => {
        if (isMonthly(productType) &&
            isIntroRateLoanPromoAvailableForAutopay(paymentEnterpriseEligibility)) {
            return t('autopay.introRateLoanAutopayText', {
                fixedPaymentAmount: billing.fixedPaymentAmount
            });
        }
        if (shouldDisplayHelpTextForPaymentAmount) {
            if (isSecuredInstallmentLoanProduct(productType)) {
                return t('autopay.autopayPaymentAmountHelpTextForSI');
            }
            return t('autopay.autopayPaymentAmountHelpText');
        }
        return '';
    };
    return (_jsxs(_Fragment, { children: [_jsx(SelectWrapper, { children: _jsx(SelectWithFocus, { autoAdjust: true, dataCy: "payment-amount-field", defaultOption: getDefaultOptions(amountOptions, paymentOptionField.value), error: paymentOptionField.error, errorIcon: _jsx(FontAwesomeIcon, { icon: FAR_EXCLAMATION_CIRCLE, size: "1x" }), helpText: autopayPaymentAmountHelpText(), id: id, isDisabled: paymentOptionField.isDisabled, leftIcon: _jsx(FontAwesomeIcon, { icon: FAR_DOLLAR_SIGN }), name: id, options: amountOptions, placeholder: t('label.paymentAmountPlaceholder'), required: true, rightIconClosed: rightIconClosed, rightIconOpen: _jsx(FontAwesomeIcon, { icon: FAR_ANGLE_UP, size: "lg" }), tabIndex: paymentOptionField.isDisabled ? -1 : 0, onChange: paymentOptionChangeHandler }) }), isOtherAmount && (_jsx(SelectWrapper, { children: _jsx(Textfield, { autoCapitalize: "none", autoComplete: "transaction-amount", dataCy: "other-amount-text-field", error: paymentAmountField.error, errorIcon: _jsx(FontAwesomeIcon, { icon: FAR_EXCLAMATION_CIRCLE, size: "1x" }), helpText: otherAmountHelpText, id: otherAmountKey, inputMode: "decimal", isDisabled: paymentAmountField.isDisabled, leftIcon: _jsx(FontAwesomeIcon, { icon: FAR_DOLLAR_SIGN }), matchPattern: /^[0-9]*(\.[0-9]{0,2})?$/, name: otherAmountKey, placeholder: t('text.otherAmountOption'), required: true, type: "text", value: paymentAmount, width: "100%", onBlur: paymentAmountBlurHandler, onChange: paymentAmountChangeHandler }) }))] }));
};
export default AutopayAmountSelect;
